import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ScanLine, ShieldCheck, MessageCircle } from "lucide-react";
import { LEGAL_LINKS } from "../legal/links";

const QR_URL = "https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=https%3A%2F%2Fwa.me%2F447383513170&color=002B7F&bgcolor=FFFFFF&margin=10";

const HelpHero = () => (
  <div className="grid md:grid-cols-12 gap-10 items-center">
    <div className="md:col-span-7 text-center md:text-left">
      <h2 className="font-display text-4xl sm:text-5xl text-[#111827] leading-[1.05] tracking-tight" data-testid="fintech-headline">
        Need more help?
      </h2>
      <p className="mt-5 text-[#6B7280] text-base max-w-md mx-auto md:mx-0 leading-relaxed">
        Get in touch with our team. Free initial 15-minute consultation. No obligation. Confidential.
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-center md:justify-start gap-3">
        <a
          href="/#contact"
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#111827] text-white font-bold text-[14.5px] hover:bg-[#002B7F] transition-colors"
          data-testid="fintech-cta-getintouch"
        >
          Get in touch <ArrowRight size={16} />
        </a>
        <a
          href="https://wa.me/447383513170"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-[#E5E7EB] text-[#111827] font-bold text-[14.5px] hover:border-[#F37021] hover:text-[#F37021] transition-colors"
          data-testid="fintech-cta-whatsapp"
        >
          <MessageCircle size={16} /> WhatsApp
        </a>
      </div>
    </div>

    <div className="md:col-span-5 flex flex-col items-center md:items-end">
      <div className="rounded-2xl bg-white p-4 border border-[#E5E7EB] shadow-[0_8px_24px_rgba(17,24,39,0.06)]" data-testid="fintech-qr">
        <img
          src={QR_URL}
          alt="Scan to message us on WhatsApp"
          width="180"
          height="180"
          className="block rounded-lg"
          loading="lazy"
        />
      </div>
      <p className="mt-4 text-[13px] text-[#6B7280] flex items-center gap-2 font-medium">
        <ScanLine size={14} className="text-[#F37021]" /> Scan to get support
      </p>
    </div>
  </div>
);

const CompanyGrid = () => (
  <div className="mt-12 grid md:grid-cols-3 gap-10">
    <div data-testid="fintech-company">
      <div className="text-[11px] font-bold tracking-[0.16em] uppercase text-[#9CA3AF]">
        Company
      </div>
      <div className="mt-3 text-[15px] text-[#111827] leading-relaxed">
        <div className="font-bold">Marioway Benefits Consultancy LTD</div>
        <div className="text-[#6B7280]">Company Number: 12784422</div>
        <div className="text-[#6B7280]">Registered in England and Wales</div>
        <div className="text-[#6B7280]">Leeds, United Kingdom</div>
      </div>
    </div>

    <div data-testid="fintech-legal">
      <div className="text-[11px] font-bold tracking-[0.16em] uppercase text-[#9CA3AF]">
        Legal
      </div>
      <p className="mt-3 text-[14px] text-[#6B7280] leading-relaxed">
        Independent administrative and consultancy services. Not affiliated with or endorsed by any UK government department or public authority.
      </p>
      <ul className="mt-3 space-y-1.5 text-[13px]">
        {LEGAL_LINKS.map((l) => (
          <li key={l.to}><Link to={l.to} className="text-[#002B7F] hover:text-[#F37021] transition-colors font-medium" data-testid={`fintech-link-${l.to.slice(1)}`}>{l.label}</Link></li>
        ))}
      </ul>
    </div>

    <div data-testid="fintech-qualifications">
      <div className="text-[11px] font-bold tracking-[0.16em] uppercase text-[#9CA3AF]">
        Founder &amp; Director
      </div>
      <p className="mt-3 text-[14px] text-[#6B7280] leading-relaxed">
        <span className="font-semibold text-[#111827]">Florin-Marius Bran</span><br />
        BSc Accounting and Financial Management<br />
        <span className="text-[12.5px] text-[#9CA3AF]">Canterbury Christ Church University · 2026</span>
      </p>
    </div>
  </div>
);

export const FintechFooter = () => (
  <section id="fintech-footer" className="bg-white border-t border-[#E6EAF0]">
    <div className="container-x py-20 md:py-24">
      <HelpHero />
      <div className="mt-16 h-px bg-[#E5E7EB]" />
      <CompanyGrid />
      {/* Confidentiality */}
      <div className="mt-12 rounded-xl bg-[#F9FAFB] border border-[#F3F4F6] p-5" data-testid="fintech-confidentiality">
        <div className="flex items-start gap-3">
          <ShieldCheck size={16} className="text-[#9CA3AF] flex-shrink-0 mt-0.5" />
          <p className="text-[12.5px] text-[#6B7280] leading-relaxed">
            <span className="font-semibold text-[#374151]">Important notice:</span>{" "}
            The free initial consultation is intended to understand the nature of your enquiry and does not itself constitute regulated legal, immigration, tax or financial advice.
            Academic qualifications are stated for transparency. Professional or regulated services are provided only where Marioway holds the appropriate authorisation or where the activity does not require regulatory authorisation.
          </p>
        </div>
      </div>

      {/* Payments */}
      <div className="mt-8 text-center" data-testid="fintech-payments">
        <p className="text-[13px] text-[#6B7280]">
          <span className="font-bold text-[#111827]">Secure Payments Accepted:</span>{" "}
          PayPal · Stripe · Square · Visa · Mastercard · Apple Pay · Google Pay · Zilch · Klarna · Clearpay · Crypto (Bitcoin)
        </p>
      </div>

      {/* Bottom */}
      <div className="mt-10 pt-8 border-t border-[#F3F4F6] text-center">
        <p className="text-[13px] text-[#9CA3AF]">
          © {new Date().getFullYear()} Marioway Benefits Consultancy LTD —{" "}
          <span className="text-[#F37021] font-semibold">Secure.</span>{" "}
          <span className="text-[#002B7F] font-semibold">Trusted.</span>{" "}
          <span className="text-[#111827] font-semibold">Professional.</span>
        </p>
      </div>
    </div>
  </section>
);
